import { setNotifyEmail } from "./api";

export interface AttachNotifyEmailArgs {
  server: string;
  runId: string;
  runToken: string;
  email: string;
}

export interface AttachNotifyEmailOpts {
  /** Total attempts including the first (default 4 = first + 3 retries). */
  attempts?: number;
  /** Backoff before retry `n` (0-indexed), in ms. */
  delayMs?: (attempt: number) => number;
  /** Injected for tests so retries don't wait on real timers. */
  sleep?: (ms: number) => Promise<void>;
  /** Injected for tests; defaults to the real setNotifyEmail API call. */
  perform?: (args: AttachNotifyEmailArgs) => Promise<unknown>;
  /** Called once on final failure (telemetry). */
  onGiveUp?: (err: unknown) => void;
}

const _DEFAULT_DELAYS = [500, 1500, 4000];

/**
 * Attach a buyer's notify-email to a just-started run, with a bounded retry.
 *
 * A self-serve buyer's `notifyEmail` is frequently the ONLY address on file
 * (self-serve codes carry no linked email), so a transient failure here must
 * not silently drop it — the buyer paid and would get no "your analysis is
 * ready" email (2026-07-07 review, ⑨). Retries a few times with backoff; on
 * final failure calls `onGiveUp` (telemetry) rather than swallowing silently.
 * The in-wizard "Change email" affordance (ChangeEmailModal) remains the manual
 * fallback. Best-effort by contract: never throws, never blocks the run.
 *
 * Returns true if the attach eventually succeeded, false if it gave up.
 */
export async function attachNotifyEmailWithRetry(
  args: AttachNotifyEmailArgs,
  opts: AttachNotifyEmailOpts = {},
): Promise<boolean> {
  const attempts = opts.attempts ?? 4;
  const delayMs =
    opts.delayMs ?? ((n) => _DEFAULT_DELAYS[n] ?? _DEFAULT_DELAYS[_DEFAULT_DELAYS.length - 1]);
  const sleep = opts.sleep ?? ((ms) => new Promise((r) => setTimeout(r, ms)));
  const perform = opts.perform ?? ((a) => setNotifyEmail(a));
  const onGiveUp =
    opts.onGiveUp ??
    ((err) =>
      console.warn(
        "[aurora] notify-email attach failed after retries; the buyer can " +
          "re-enter it via the Change-email affordance.",
        err,
      ));

  for (let attempt = 0; attempt < attempts; attempt++) {
    try {
      await perform(args);
      return true;
    } catch (err) {
      if (attempt === attempts - 1) {
        onGiveUp(err);
        return false;
      }
      await sleep(delayMs(attempt));
    }
  }
  return false;
}
